import type { Ssm5DiagramModule, Ssm5TripTime } from '@packages/domain/ssm5';

import { colors } from '@packages/core/styles';
import { DetailViewAside } from '@web/components/detail-view';
import { useCallback, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { Ssm5DtcFilterKey, Ssm5DtcFilters } from '../helpers/dtc-filters';

import { Ssm5ModuleDtcsTab } from './ssm5-module-dtcs-tab';
import { Ssm5ModuleHeader } from './ssm5-module-header';
import { Ssm5ModuleInfoTab } from './ssm5-module-info-tab';

/** Props for {@link Ssm5ModulePanel}. */
export type Ssm5ModulePanelProps = {
    /** Which filters of the DTC list are switched on. */
    filters: Ssm5DtcFilters;
    /** The abbreviation of the module, as its cell shows it, e.g. "TCU / ISS". */
    label: string;
    /** The module the panel shows. */
    module: Ssm5DiagramModule;
    /** The full name of the module, when one is known. */
    name?: string;
    /** Called when the back button is pressed. */
    onBack: () => void;
    /** Receives the filter and its checked state when a switch is pressed. */
    onFilterChange: (key: Ssm5DtcFilterKey, isChecked: boolean) => void;
    /** The vehicle's supply voltage, when it reported a valid one. */
    supplyVoltage?: number;
    /** The vehicle's current ignition cycle and the time into it, when the DTC read reported them. */
    tripTime?: Ssm5TripTime;
    /** The VIN of the session. */
    vin: string;
};

/** The tabs of the panel, keyed as the tab control knows them. */
type TabKey = 'dtcCodes' | 'generalInfo';

const FIRST_TAB: TabKey = 'dtcCodes';

const useStyles = createUseStyles({
    // The shared aside brings the gray column with its left border and its own scroll. Its width and
    // padding are overridden at a higher specificity, so the result does not depend on the sheet order.
    // The tab bodies fill the column under the tabs, so a notice can sit at the bottom of the panel.
    panel: {
        '& .tab-content': {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
        },
        '& .tab-content .tab-pane.active': {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            paddingTop: 0,
        },
        '&&': {
            padding: 0,
            width: 440,
        },
    },
    // The design's small navigation tabs over the shared tab rules: labels in Body/Semibold between 16px of
    // padding, the open one in the brand color under its 2px line, the other in light gray. The shared
    // rules match at the same specificity, so these double the class to win whatever the sheet order.
    // The shared bar scrolls its content, and the open tab's line sits 2px into the bar's border, which a
    // scrolling bar shows as a scrollbar when the system draws them; this bar never scrolls.
    tabs: {
        '&&.nav-tabs': {
            overflow: 'visible',
            padding: '0 16px 0 15px',
        },
        '&&.nav-tabs .nav-item.nav-link': {
            color: colors.grayFive,
            fontSize: 15,
            fontWeight: 600,
            lineHeight: '24px',
            padding: '16px 0',
        },
        '&&.nav-tabs .nav-item.nav-link.active': {
            color: colors.blueOne,
            fontWeight: 600,
        },
    },
});

/**
 * The side panel of one module of the CAN Bus diagram: its header, then the DTC Codes and General Info
 * tabs, DTC Codes open first.
 *
 * @param props - {@link Ssm5ModulePanelProps}
 * @returns the panel
 */
export function Ssm5ModulePanel({
    filters,
    label,
    module,
    name,
    onBack,
    onFilterChange,
    supplyVoltage,
    tripTime,
    vin,
}: Ssm5ModulePanelProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const [tab, setTab] = useState<string>(FIRST_TAB);
    const selectTab = useCallback((key: string | null) => {
        if (key !== null) {
            setTab(key);
        }
    }, []);

    return (
        <DetailViewAside classNames={classes.panel}>
            <Ssm5ModuleHeader label={label} module={module} name={name} onBack={onBack} />
            <Tabs activeKey={tab} className={classes.tabs} id="ssm5-module-tabs" onSelect={selectTab}>
                <Tab eventKey="dtcCodes" title={t('ssm5:module.tabs.dtcCodes')}>
                    <Ssm5ModuleDtcsTab filters={filters} module={module} onFilterChange={onFilterChange} vin={vin} />
                </Tab>
                <Tab eventKey="generalInfo" title={t('ssm5:module.tabs.generalInfo')}>
                    <Ssm5ModuleInfoTab module={module} supplyVoltage={supplyVoltage} tripTime={tripTime} />
                </Tab>
            </Tabs>
        </DetailViewAside>
    );
}
